import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Gift, 
  Star,
  Settings,
  Zap,
  Scissors,
  Sparkles,
  Palette,
  Trophy,
  Target,
  Save,
  Users,
  AlertCircle,
  CheckCircle,
  Percent,
  Award
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const SERVICE_TIERS = [
  { 
    id: "express", 
    name: "Tier 1: Express", 
    description: "Trims, Line-ups, Bang Trims",
    icon: Zap,
    color: "bg-blue-100 text-blue-700",
    defaultPoints: 3
  },
  { 
    id: "standard", 
    name: "Tier 2: Standard", 
    description: "Haircuts, Basic Styling",
    icon: Scissors,
    color: "bg-green-100 text-green-700",
    defaultPoints: 5
  },
  { 
    id: "premium", 
    name: "Tier 3: Premium", 
    description: "Full Styles, Blowouts, Special Occasion",
    icon: Sparkles,
    color: "bg-purple-100 text-purple-700",
    defaultPoints: 10
  },
  { 
    id: "color", 
    name: "Tier 4: Color/Chemical", 
    description: "Color, Treatments, Relaxers, Perms",
    icon: Palette,
    color: "bg-amber-100 text-amber-700",
    defaultPoints: 15
  }
];

const DEFAULT_MILESTONES = [
  {
    id: "milestone_a",
    name: "50% Discount",
    points_required: 100,
    reward_type: "percentage_discount",
    reward_value: 50,
    applies_to: "basic_services",
    excludes: []
  },
  {
    id: "milestone_b",
    name: "Free Service",
    points_required: 200,
    reward_type: "free_service",
    reward_value: 100,
    applies_to: "eligible_services",
    excludes: ["weaves", "braids", "extensions"]
  }
];

const RewardsCommandCenter = ({ token, user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    enabled: true,
    allow_individual_control: false,
    point_tiers: {
      express: 3,
      standard: 5,
      premium: 10,
      color: 15
    },
    milestones: DEFAULT_MILESTONES,
    terms_and_conditions: "Points expire after 12 months of inactivity. Rewards cannot be combined with other offers. High-labor services (Braids, Weaves, Extensions) are excluded from free service rewards. Management reserves the right to modify the program at any time."
  });
  const [stats, setStats] = useState({
    total_points_issued: 0,
    total_rewards_redeemed: 0,
    active_members: 0
  });

  useEffect(() => {
    fetchRewardsConfig();
  }, [token]);

  const fetchRewardsConfig = async () => {
    try {
      const [configRes, statsRes] = await Promise.all([
        axios.get(`${API}/loyalty/config`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/loyalty/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (configRes.data) {
        setConfig(prev => ({ ...prev, ...configRes.data }));
      }
      if (statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch rewards config:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/loyalty/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Rewards configuration saved!");
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const updateTierPoints = (tierId, points) => {
    setConfig(prev => ({
      ...prev,
      point_tiers: {
        ...prev.point_tiers,
        [tierId]: parseInt(points) || 0
      }
    }));
  };

  const updateMilestone = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      milestones: prev.milestones.map((m, i) => 
        i === index ? { ...m, [field]: value } : m
      )
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Trophy className="w-7 h-7 text-amber-500" />
            Rewards Command Center
          </h2>
          <p className="text-gray-500">Configure your automated loyalty program</p>
        </div>
        <Button 
          onClick={saveConfig}
          disabled={saving}
          className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Gift className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{stats.total_points_issued?.toLocaleString() || 0}</p>
            <p className="text-xs text-gray-500">Points Issued</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Award className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{stats.total_rewards_redeemed || 0}</p>
            <p className="text-xs text-gray-500">Rewards Redeemed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-800">{stats.active_members || 0}</p>
            <p className="text-xs text-gray-500">Active Members</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="settings">Master Settings</TabsTrigger>
          <TabsTrigger value="points">Point Architect</TabsTrigger>
          <TabsTrigger value="milestones">Reward Milestones</TabsTrigger>
          <TabsTrigger value="terms">Terms & Conditions</TabsTrigger>
        </TabsList>

        {/* Master Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-500" />
                The Master Toggle
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Main Toggle */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    config.enabled ? "bg-green-100" : "bg-gray-100"
                  }`}>
                    {config.enabled ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">Enable Rewards Program</h3>
                    <p className="text-sm text-gray-500">
                      {config.enabled 
                        ? "Clients are earning points on every visit" 
                        : "Rewards program is currently disabled"
                      }
                    </p>
                  </div>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
                  className="data-[state=checked]:bg-green-500"
                />
              </div>

              {/* Individual Stylist Control (Owner Only) */}
              {user?.role === "owner" && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-800">Individual Stylist Control</h3>
                      <p className="text-sm text-gray-500">
                        Allow booth renters to set their own point rules
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={config.allow_individual_control}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, allow_individual_control: checked }))}
                  />
                </div>
              )}

              {/* Status Indicator */}
              <div className={`p-4 rounded-xl ${config.enabled ? "bg-green-50 border border-green-200" : "bg-gray-50 border border-gray-200"}`}>
                <div className="flex items-center gap-2">
                  {config.enabled ? (
                    <>
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-medium text-green-700">Rewards Program Active</span>
                    </>
                  ) : (
                    <>
                      <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                      <span className="font-medium text-gray-600">Rewards Program Inactive</span>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Point Architect Tab */}
        <TabsContent value="points">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-orange-500" />
                The Point Architect
              </CardTitle>
              <p className="text-sm text-gray-500">Set how many points clients earn per service tier</p>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {SERVICE_TIERS.map((tier) => {
                  const Icon = tier.icon;
                  return (
                    <div key={tier.id} className="p-4 border rounded-xl hover:shadow-md transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tier.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-800">{tier.name}</h4>
                            <p className="text-xs text-gray-500">{tier.description}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={config.point_tiers[tier.id] || tier.defaultPoints}
                          onChange={(e) => updateTierPoints(tier.id, e.target.value)}
                          className="w-20 text-center font-bold"
                        />
                        <span className="text-gray-600 font-medium">points per service</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Quick Preview */}
              <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <h4 className="font-medium text-amber-800 mb-2">Point Preview</h4>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{config.point_tiers.express || 3}</p>
                    <p className="text-amber-700">Express</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{config.point_tiers.standard || 5}</p>
                    <p className="text-amber-700">Standard</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{config.point_tiers.premium || 10}</p>
                    <p className="text-amber-700">Premium</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{config.point_tiers.color || 15}</p>
                    <p className="text-amber-700">Color</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reward Milestones Tab */}
        <TabsContent value="milestones">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Reward Milestones
              </CardTitle>
              <p className="text-sm text-gray-500">Configure reward thresholds and what clients earn</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {config.milestones.map((milestone, index) => (
                <div key={milestone.id} className="p-4 border-2 rounded-xl hover:border-orange-300 transition-all">
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      index === 0 ? "bg-green-100" : "bg-purple-100"
                    }`}>
                      {index === 0 ? (
                        <Percent className="w-7 h-7 text-green-600" />
                      ) : (
                        <Gift className="w-7 h-7 text-purple-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800 text-lg">
                        Milestone {String.fromCharCode(65 + index)}: {milestone.name}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {index === 0 
                          ? "Percentage discount on eligible services"
                          : "Completely free service (exclusions apply)"
                        }
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-600">Points Required</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          min="10"
                          max="1000"
                          value={milestone.points_required}
                          onChange={(e) => updateMilestone(index, "points_required", parseInt(e.target.value) || 0)}
                          className="w-24 font-bold text-center"
                        />
                        <span className="text-gray-600">points</span>
                      </div>
                    </div>
                    
                    {milestone.reward_type === "percentage_discount" && (
                      <div>
                        <Label className="text-gray-600">Discount Percentage</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            min="5"
                            max="100"
                            value={milestone.reward_value}
                            onChange={(e) => updateMilestone(index, "reward_value", parseInt(e.target.value) || 0)}
                            className="w-24 font-bold text-center"
                          />
                          <span className="text-gray-600">% OFF</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Exclusions */}
                  {milestone.excludes?.length > 0 && (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-700">
                        <strong>Excludes:</strong> {milestone.excludes.map(e => e.charAt(0).toUpperCase() + e.slice(1)).join(", ")}
                      </p>
                    </div>
                  )}

                  {/* Applies To */}
                  <div className="mt-3">
                    <Badge variant="secondary" className="text-xs">
                      Applies to: {milestone.applies_to === "basic_services" ? "Basic Services Only" : "All Eligible Services"}
                    </Badge>
                  </div>
                </div>
              ))}

              {/* Visual Milestone Chart */}
              <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl">
                <h4 className="font-medium text-gray-800 mb-4">Reward Journey</h4>
                <div className="relative">
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div className="h-2 bg-gradient-to-r from-green-500 to-purple-500 rounded-full" style={{ width: "100%" }}></div>
                  </div>
                  <div className="flex justify-between mt-2">
                    <div className="text-center">
                      <div className="w-4 h-4 bg-green-500 rounded-full mx-auto mb-1"></div>
                      <p className="text-xs font-medium text-gray-600">0 pts</p>
                      <p className="text-xs text-gray-400">Start</p>
                    </div>
                    <div className="text-center">
                      <div className="w-4 h-4 bg-green-500 rounded-full mx-auto mb-1"></div>
                      <p className="text-xs font-medium text-gray-600">{config.milestones[0]?.points_required} pts</p>
                      <p className="text-xs text-gray-400">{config.milestones[0]?.reward_value}% Off</p>
                    </div>
                    <div className="text-center">
                      <div className="w-4 h-4 bg-purple-500 rounded-full mx-auto mb-1"></div>
                      <p className="text-xs font-medium text-gray-600">{config.milestones[1]?.points_required} pts</p>
                      <p className="text-xs text-gray-400">Free Service</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Terms & Conditions Tab */}
        <TabsContent value="terms">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-gray-500" />
                Terms & Conditions
              </CardTitle>
              <p className="text-sm text-gray-500">This text appears on client receipts</p>
            </CardHeader>
            <CardContent>
              <textarea
                value={config.terms_and_conditions}
                onChange={(e) => setConfig(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                className="w-full h-40 p-4 border rounded-xl resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Enter your rewards program terms and conditions..."
              />
              <p className="mt-2 text-xs text-gray-400">
                This will be displayed at the bottom of digital receipts sent to clients.
              </p>

              {/* Preview */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl border">
                <h5 className="font-medium text-gray-700 mb-2">Preview on Receipt:</h5>
                <p className="text-xs text-gray-500 italic">
                  {config.terms_and_conditions}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RewardsCommandCenter;
